import { useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react"
import { router, usePage } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, Copy, Database, EyeOff, Info, Lock, MoreVertical, Sparkles, Trash2, Wrench, X, XCircle } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/cn"
import { ForgeOnFailure } from "@/components/domain/forge-on-failure"
import { ForgeQuickChat } from "@/components/domain/forge-quick-chat"
import { ForgeProposalReview } from "@/components/domain/forge-proposal-review"
import { TestConversationMessageCard } from "@/components/domain/test-conversation-message-card"
import { upsertProposalByTarget, type Proposal } from "@/components/domain/proposal-diff-card"
import { JsonEditor } from "@/components/ui/json-editor"
import { Dialog } from "@/components/ui/dialog"
import type { SharedProps } from "@/types/inertia"
import type { ChatBubble, TestLabAssertionVerdict, TestLabScenario, TestRunAppuserContext, TestRunDetail, TestRunFinalState, TestRunModelRequestSummary, TestRunStatus, WorkflowAllyContext, WorkflowTraceEvent, WorkflowTracePayload, WorkflowTraceRequirement } from "@/types/test_lab"

const RUN_DETAIL_POLL_MS = 5000
const ACTIVE_RUN_STATUSES = new Set(["pending", "running"])

type Props = {
  skillId?: number | null            // parent Skill id when this is skill-owned
  skillVersionId?: number | null
  scenario: TestLabScenario
  onFix: () => Promise<void> | void  // wraps TestLab's discussFailure(runId)
  /**
   * How the Conversation tab handles a failed run:
   *   - "callout" (default): yellow "Fix with Forge" button → onFix
   *   - "chat":   embedded Forge chat (Test Lab Test tab today)
   *   - "none":   render nothing (parent already has Forge in another pane,
   *               e.g. dual-pane rehearsal-results modal)
   */
  failurePaneMode?: "callout" | "chat" | "none"
  runDetailPath?: (runId: number) => string
  discussPayloadPath?: (runId: number) => string
  scenarioUpdatePath?: (scenarioId: number) => string
  scenarioDeletePath?: (scenarioId: number) => string
}

type DetailTab = "forge" | "conversation" | "assertions" | "workflow" | "run_details" | "mocks"

type TabSpec = { id: DetailTab; label: string; icon?: typeof Sparkles }

const BASE_TABS: TabSpec[] = [
  { id: "conversation", label: "Conversation" },
  { id: "assertions",   label: "Assertions"   },
  { id: "workflow",     label: "Workflow"     },
  { id: "run_details",  label: "Run details"  },
  { id: "mocks",        label: "Mocks"        },
]

export function ScenarioDetail({
  skillId = null,
  skillVersionId = null,
  scenario,
  onFix,
  failurePaneMode = "callout",
  runDetailPath,
  discussPayloadPath,
  scenarioUpdatePath,
  scenarioDeletePath,
}: Props) {
  const { props } = usePage<SharedProps>()
  const isAdmin = props.isAdmin === true
  const resolvedRunDetailPath = useMemo(
    () => runDetailPath ?? ((id: number) => `/skills/${skillId}/test_runs/${id}`),
    [runDetailPath, skillId],
  )
  const resolvedDiscussPayloadPath = useMemo(
    () => discussPayloadPath ?? ((id: number) => `/skills/${skillId}/test_runs/${id}/discuss_payload`),
    [discussPayloadPath, skillId],
  )
  const resolvedScenarioUpdatePath = useMemo(
    () => scenarioUpdatePath ?? (skillId != null ? ((id: number) => `/skills/${skillId}/scenarios/${id}`) : undefined),
    [scenarioUpdatePath, skillId],
  )
  const resolvedScenarioDeletePath = useMemo(
    () => scenarioDeletePath ?? (skillId != null ? ((id: number) => `/skills/${skillId}/test_scenarios/${id}`) : undefined),
    [scenarioDeletePath, skillId],
  )
  const canDeleteScenario = props.capabilities?.includes("skills_edit") === true && resolvedScenarioDeletePath != null
  const [run, setRun]       = useState<TestRunDetail | null>(null)
  const [loading, setLoad]  = useState(false)
  const [deleting, setDeleting] = useState(false)

  const runId = scenario.latestRunId
  const failed = scenario.latestRunStatus === "failed" || scenario.latestRunStatus === "errored"
  // The Forge tab is part of the Test Lab detail surface. It stays visible
  // as the second tab so operators always know where to ask about a run;
  // the body handles "run this first" / "no failure context" states.
  const showForgeTab = failurePaneMode === "chat" && skillId != null
  const baseTabs = BASE_TABS.filter((t) => isAdmin || t.id !== "run_details")
  const tabs: TabSpec[] = showForgeTab
    ? [
        baseTabs[0],
        { id: "forge", label: "Forge", icon: Sparkles },
        ...baseTabs.slice(1),
      ]
    : baseTabs

  // Default landing tab is always Conversation. Forge sits second as the
  // next action after reading the recorded transcript.
  const [tab, setTab] = useState<DetailTab>("conversation")

  // If the operator switches scenarios and ends up on a tab that no
  // longer exists (e.g. they were on Forge, then picked a green
  // scenario where Forge is hidden), snap back to Conversation.
  useEffect(() => {
    if (tab === "forge" && !showForgeTab) setTab("conversation")
    if (tab === "run_details" && !isAdmin) setTab("conversation")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario.id, showForgeTab, isAdmin])

  // Forge-tab state: proposals streamed from the chat, plus the seed
  // payload (skillSnapshot + scenarios) needed for diff cards. Reset on
  // scenario change so the proposals from a previous failure don't
  // bleed into the next one.
  const [forgeProposals, setForgeProposals]   = useState<Proposal[]>([])
  const [forgeSnapshot,  setForgeSnapshot]    = useState<Record<string, unknown> | null>(null)
  const [forgeScenarios, setForgeScenariosState] = useState<Array<Record<string, unknown>>>([])
  useEffect(() => {
    setForgeProposals([])
    setForgeSnapshot(null)
    setForgeScenariosState([])
  }, [scenario.id])
  const isDraft = (forgeSnapshot?.state as string | undefined) === "draft"

  // Fetch the run detail (transcript + verdicts + cost) whenever the
  // selected scenario's latest run id changes. The list pre-loads the
  // scenario's metadata; the detail body needs the message rows.
  useEffect(() => {
    if (runId == null) { setRun(null); return }
    let cancelled = false
    setLoad(true)
    fetch(resolvedRunDetailPath(runId), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: TestRunDetail = await res.json()
        if (!cancelled) setRun(data)
      })
      .catch(() => { if (!cancelled) setRun(null) })
      .finally(() => { if (!cancelled) setLoad(false) })
    return () => { cancelled = true }
  }, [runId, resolvedRunDetailPath])

  useEffect(() => {
    if (runId == null) return
    if (!isActiveRunStatus(scenario.latestRunStatus) && !isActiveRunStatus(run?.status)) return

    const id = window.setInterval(() => {
      fetch(resolvedRunDetailPath(runId), {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setRun(data) })
        .catch(() => {/* keep last good */})
    }, RUN_DETAIL_POLL_MS)

    return () => window.clearInterval(id)
  }, [runId, run?.status, scenario.latestRunStatus, resolvedRunDetailPath])

  const messages = run?.messages ?? []
  const verdicts = run?.verdicts.length ? run.verdicts : scenario.latestRunVerdicts

  async function deleteScenario() {
    if (deleting) return
    if (!canDeleteScenario) {
      toast.error("You don't have permission to delete tests.")
      return
    }
    const label = scenario.name || scenario.key
    if (!confirm(`Delete test "${label}"? This cannot be undone.`)) return

    setDeleting(true)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      if (!resolvedScenarioDeletePath) return
      const res = await fetch(resolvedScenarioDeletePath(scenario.id), {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrf,
        },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        toast.error(data.error || `Couldn't delete test (HTTP ${res.status})`)
        return
      }
      toast.success("Test deleted")
      router.reload()
    } catch {
      toast.error("Network error — try again")
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="valet-panel flex h-full min-h-[620px] flex-col overflow-hidden rounded-card lg:h-[calc(100vh-220px)] lg:min-h-0">
      <Header
        scenario={scenario}
        isAdmin={isAdmin}
        scenarioUpdatePath={resolvedScenarioUpdatePath}
        onDelete={deleteScenario}
        canDelete={canDeleteScenario}
        deleting={deleting}
      />

      {/* Tab row: "Forge" brand chip on the left, then pill-styled tabs.
          flex-wrap so the tabs reflow to a second line when the rail is
          narrow (mobile + the 380px fixed-rail layout side-by-side). */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-4">
        <div className="flex flex-wrap items-center gap-1 py-2">
          {tabs.map((t) => {
            const Icon = t.icon
            const isForge = t.id === "forge"
            const isActive = tab === t.id
            return (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                title={t.id === "run_details" ? "Admin-only; hidden from non-admin users" : undefined}
                className={cn(
                  "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition",
                  isActive
                    ? isForge
                      ? "forge-tab-active border-b-2 font-semibold"
                      : "border-brand-red font-semibold text-white"
                    : isForge
                      ? "border-transparent text-vip-goldSoft/75 hover:text-vip-goldSoft"
                      : "border-transparent text-app-muted hover:text-app-text"
                )}
              >
                {Icon && (
                  <Icon
                    size={14}
                    strokeWidth={1.8}
                    className={cn(isForge && "forge-icon")}
                  />
                )}
                {t.id === "run_details" && (
                  <EyeOff size={13} className="text-app-subtle" />
                )}
                {t.label}
                {t.id === "assertions" && verdicts.length > 0 && (() => {
                  const passed = verdicts.filter((v) => v.passed).length
                  const allPassed = passed === verdicts.length
                  return (
                    <span
                      className={cn(
                        "ml-1 text-[11px] font-medium",
                        allPassed ? "text-status-success" : "text-status-error",
                      )}
                    >
                      {passed}/{verdicts.length}
                    </span>
                  )
                })()}
              </button>
            )
          })}
        </div>
      </div>

      <div className={cn(
        "thin-scrollbar min-h-0 flex-1 p-4",
        tab === "forge" ? "overflow-hidden" : "overflow-y-auto",
      )}>
        {tab === "forge" && showForgeTab && (
          <ForgeTab
            skillId={skillId!}
            skillVersionId={skillVersionId}
            runId={runId}
            discussPayloadPath={resolvedDiscussPayloadPath}
            forgeProposals={forgeProposals}
            setForgeProposals={setForgeProposals}
            forgeSnapshot={forgeSnapshot}
            setForgeSnapshot={setForgeSnapshot}
            forgeScenarios={forgeScenarios}
            setForgeScenariosState={setForgeScenariosState}
            isDraft={isDraft}
            scenario={scenario}
          />
        )}
        {tab === "conversation" && (
          <ConversationTab
            scenario={scenario}
            run={run}
            messages={messages}
            failed={failed}
            loading={loading}
            onFix={onFix}
            failurePaneMode={failurePaneMode}
          />
        )}
        {tab === "assertions"  && <AssertionsTab verdicts={verdicts} hasRun={runId != null} />}
        {tab === "workflow"    && <WorkflowTraceTab trace={run?.workflowTrace ?? null} loading={loading} hasRun={runId != null} />}
        {tab === "run_details" && isAdmin && <RunDetailsTab run={run} scenario={scenario} loading={loading} />}
        {tab === "mocks"       && <MocksTab scenario={scenario} scenarioUpdatePath={resolvedScenarioUpdatePath} />}
      </div>
    </div>
  )
}

function ForgeTab({
  skillId,
  skillVersionId,
  runId,
  discussPayloadPath,
  forgeProposals,
  setForgeProposals,
  forgeSnapshot,
  setForgeSnapshot,
  forgeScenarios,
  setForgeScenariosState,
  isDraft,
  scenario,
}: {
  skillId: number
  skillVersionId: number | null
  runId: number | null
  discussPayloadPath: (runId: number) => string
  forgeProposals: Proposal[]
  setForgeProposals: (next: Proposal[] | ((cur: Proposal[]) => Proposal[])) => void
  forgeSnapshot: Record<string, unknown> | null
  setForgeSnapshot: (next: Record<string, unknown> | null) => void
  forgeScenarios: Array<Record<string, unknown>>
  setForgeScenariosState: (next: Array<Record<string, unknown>>) => void
  isDraft: boolean
  scenario: TestLabScenario
}) {
  const [panel, setPanel] = useState<"forge" | "approval">("forge")

  return (
    <div className="flex h-full min-h-[640px] flex-col lg:min-h-0">
      <div className="mb-3 flex items-center gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setPanel("forge")}
          className={cn(
            "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition",
            panel === "forge"
              ? "forge-tab-active font-semibold"
              : "border-transparent text-vip-goldSoft/75 hover:text-vip-goldSoft",
          )}
        >
          <Sparkles size={14} className="forge-icon" />
          Forge
        </button>
        <button
          type="button"
          onClick={() => setPanel("approval")}
          className={cn(
            "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition",
            panel === "approval"
              ? "border-brand-red font-semibold text-white"
              : "border-transparent text-app-muted hover:text-app-text",
          )}
        >
          Approval
          {forgeProposals.length > 0 && (
            <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-app-muted">
              {forgeProposals.length}
            </span>
          )}
        </button>
      </div>

      <div className={cn("min-h-0 flex-1 overflow-hidden", panel !== "forge" && "hidden")}>
        <ForgeQuickChat
          skillId={skillId}
          skillVersionId={skillVersionId}
          runId={runId}
          discussPayloadPath={discussPayloadPath}
          seedMessage={runId == null ? buildScenarioForgeSeed(scenario) : null}
          onProposal={(p) => setForgeProposals((cur) => upsertProposalByTarget(cur, { ...p, accepted: true }))}
          onSeedLoaded={(payload) => {
            if (payload.skillSnapshot) setForgeSnapshot(payload.skillSnapshot)
            if (payload.scenarios) setForgeScenariosState(payload.scenarios)
          }}
        />
      </div>

      <div className={cn("thin-scrollbar min-h-0 flex-1 overflow-y-auto", panel !== "approval" && "hidden")}>
        <ForgeProposalReview
          skillId={skillId}
          proposals={forgeProposals}
          setProposals={setForgeProposals}
          skillSnapshot={forgeSnapshot}
          scenarios={forgeScenarios}
          isDraft={isDraft}
          onSnapshotChange={(s) => setForgeSnapshot(s)}
        />
      </div>
    </div>
  )
}

function buildScenarioForgeSeed(scenario: TestLabScenario): string {
  const parts = [
    `I want to discuss or change this saved test scenario before running it.`,
    `Scenario key: \`${scenario.key}\``,
    `Scenario name: ${scenario.name || scenario.key}`,
    `Mode: ${scenario.mode}`,
  ]
  if (scenario.goal) parts.push(`Goal: ${scenario.goal}`)
  if (scenario.persona) parts.push(`Emulated person / persona: ${scenario.persona}`)
  if (scenario.category) parts.push(`Category: ${scenario.category}`)
  if (scenario.scope) parts.push(`Scope: ${scenario.scope}`)
  if (scenario.allowedToolCalls?.length) {
    parts.push(`Allowed workflow activity: ${scenario.allowedToolCalls.join(", ")}`)
  }
  parts.push(
    "Help me decide whether the test itself should change, whether the skill should change, or whether I should run it as-is. Offer options first; do not emit proposal tool calls until I choose one.",
  )
  return parts.join("\n\n")
}

function Header({
  scenario,
  isAdmin,
  scenarioUpdatePath,
  onDelete,
  canDelete,
  deleting,
}: {
  scenario: TestLabScenario
  isAdmin: boolean
  scenarioUpdatePath?: (scenarioId: number) => string
  onDelete: () => void
  canDelete: boolean
  deleting: boolean
}) {
  const status = scenario.latestRunStatus
  const [menuOpen, setMenuOpen] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [draftName, setDraftName] = useState(scenario.name || scenario.key)
  const [displayName, setDisplayName] = useState(scenario.name || scenario.key)
  const [savingName, setSavingName] = useState(false)

  useEffect(() => {
    setDraftName(scenario.name || scenario.key)
    setDisplayName(scenario.name || scenario.key)
    setEditingName(false)
  }, [scenario.id, scenario.key, scenario.name])

  function chooseDelete() {
    setMenuOpen(false)
    onDelete()
  }

  function cancelNameEdit() {
    setDraftName(displayName)
    setEditingName(false)
  }

  async function saveName() {
    if (savingName) return

    const name = draftName.trim()
    if (name.length === 0) {
      toast.error("Scenario name can't be blank")
      setDraftName(scenario.name || scenario.key)
      setEditingName(false)
      return
    }
    if (name === displayName) {
      setEditingName(false)
      return
    }
    if (!scenarioUpdatePath) {
      toast.error("This scenario can't be renamed here.")
      setDraftName(displayName)
      setEditingName(false)
      return
    }

    setSavingName(true)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch(scenarioUpdatePath(scenario.id), {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ name }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.error || `Couldn't rename scenario (HTTP ${res.status})`)
        return
      }

      toast.success("Scenario renamed")
      setDisplayName(name)
      setDraftName(name)
      setEditingName(false)
      router.reload({ only: ["scenarios"] })
    } catch {
      toast.error("Network error — try again")
    } finally {
      setSavingName(false)
    }
  }

  function handleNameKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
    }
    if (event.key === "Escape") {
      event.preventDefault()
      cancelNameEdit()
    }
  }

  return (
    <div className="border-b border-border p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-3">
            <HeaderGlyph status={status} />
            <h1 className="min-w-0 text-xl font-semibold text-white">
              {editingName ? (
                <input
                  autoFocus
                  value={draftName}
                  disabled={savingName}
                  onChange={(event) => setDraftName(event.currentTarget.value)}
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={() => void saveName()}
                  onKeyDown={handleNameKeyDown}
                  className="h-10 w-[min(760px,70vw)] max-w-full rounded-control border border-brand-redBorder bg-black/35 px-2 text-xl font-semibold text-white outline-none disabled:opacity-60"
                />
              ) : canDelete ? (
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  title="Edit scenario name"
                  className="block max-w-full truncate rounded-control text-left transition hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-redBorder"
                >
                  {displayName}
                </button>
              ) : (
                <span className="block max-w-full truncate">{displayName}</span>
              )}
            </h1>
            <span
              className={cn("shrink-0 rounded-control border px-2 py-1 text-xs font-semibold capitalize", headerStatusTone(status))}
              title={scenario.latestRunSummary ?? undefined}
            >
              {headerStatusLabel(status)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-app-muted">
            <CopyMetaChip label={scenario.key} copyValue={scenario.key} title="Copy scenario key" />
            {isAdmin && scenario.latestRunId != null && (
              <CopyMetaChip label={`run #${scenario.latestRunId}`} copyValue={scenario.latestRunId} title="Copy latest test run ID" />
            )}
            {isAdmin && scenario.latestRunConvoId != null && (
              <CopyMetaChip label={`convo ${scenario.latestRunConvoId}`} copyValue={scenario.latestRunConvoId} title="Copy latest run AgentConvo bigint ID" />
            )}
            {isAdmin && scenario.latestRunBatchId != null && (
              <CopyMetaChip label={`batch #${scenario.latestRunBatchId}`} copyValue={scenario.latestRunBatchId} title="Copy latest run TestBatch ID" />
            )}
            {scenario.category && <span className="rounded-control border border-border px-1.5 py-0.5">{scenario.category}</span>}
            {scenario.scope && <span className="rounded-control border border-border px-1.5 py-0.5">{scenario.scope}</span>}
            {scenario.maxTurns && <span>max {scenario.maxTurns} turns</span>}
          </div>
        </div>
        <div className="relative flex gap-2">
          <button
            type="button"
            aria-label="Scenario actions"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-control border border-border bg-white/[0.04] text-app-muted transition hover:bg-white/[0.07] hover:text-white"
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-11 z-20 w-48 overflow-hidden rounded-control border border-border bg-app-panel shadow-panel">
              <button
                type="button"
                onClick={chooseDelete}
                disabled={!canDelete || deleting}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-status-error transition hover:bg-status-error/10 disabled:cursor-not-allowed disabled:opacity-50"
                title={canDelete ? "Delete this saved test" : "Requires skill edit permission"}
              >
                <Trash2 size={14} />
                {deleting ? "Deleting…" : "Delete test"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-3 text-xs sm:grid-cols-2 2xl:hidden">
        {scenario.goal && (
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">Goal</div>
            <div className="text-app-text">{scenario.goal}</div>
          </div>
        )}
        {scenario.persona && (
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">Persona</div>
            <div className="text-app-text">{scenario.persona}</div>
          </div>
        )}
        {scenario.allowedToolCalls?.length > 0 && (
          <div className="sm:col-span-2">
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">Allowed workflow activity (ok if used, not required)</div>
            <div className="flex flex-wrap gap-1">
              {scenario.allowedToolCalls.map((t) => (
                <code key={t} className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-app-text">{t}</code>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CopyMetaChip({ label, copyValue, title }: { label: string; copyValue: string | number; title: string }) {
  function copy() {
    const value = String(copyValue)
    if (!navigator.clipboard) {
      toast.error("Clipboard is not available")
      return
    }

    void navigator.clipboard.writeText(value).then(
      () => toast.success(`Copied ${label}`),
      () => toast.error("Could not copy")
    )
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={title}
      className="inline-flex min-h-5 items-center gap-1 rounded bg-white/5 px-1 text-app-muted transition hover:bg-white/10 hover:text-white"
    >
      <code>{label}</code>
      <Copy size={10} aria-hidden="true" />
    </button>
  )
}

function HeaderGlyph({ status }: { status: TestRunStatus | null }) {
  const classes = cn(
    "grid h-7 w-7 shrink-0 place-items-center rounded-full border",
    status === "passed" && "border-status-success/50 bg-status-success/10 text-status-success",
    status === "failed" && "border-status-error/50 bg-status-error/10 text-status-error",
    status === "errored" && "border-status-warning/50 bg-status-warning/10 text-status-warning",
    !status && "border-border bg-white/[0.04] text-app-muted",
  )
  if (status === "passed") return <span className={classes}><CheckCircle2 size={15} /></span>
  if (status === "errored") return <span className={classes}><AlertTriangle size={15} /></span>
  if (status === "failed") return <span className={classes}><XCircle size={15} /></span>
  return <span className={classes}>•</span>
}

function headerStatusLabel(status: TestRunStatus | null): string {
  if (!status) return "Not run"
  if (status === "errored") return "Errored"
  return status
}

function headerStatusTone(status: TestRunStatus | null): string {
  switch (status) {
    case "passed":
      return "border-status-success/50 bg-status-success/10 text-status-success"
    case "failed":
      return "border-status-error/50 bg-status-error/10 text-status-error"
    case "errored":
      return "border-status-warning/50 bg-status-warning/10 text-status-warning"
    default:
      return "border-border bg-white/[0.04] text-app-muted"
  }
}

function ConversationTab({ scenario, run, messages, failed, loading, onFix, failurePaneMode }:
  {
    scenario: TestLabScenario
    run: TestRunDetail | null
    messages: ChatBubble[]
    failed: boolean
    loading: boolean
    onFix: () => Promise<void> | void
    failurePaneMode: "callout" | "chat" | "none"
  }) {
  if (scenario.latestRunId == null) {
    return (
      <div className="p-6 text-center text-sm text-app-muted">
        This scenario hasn't been run yet. Click <span className="text-app-text">Run</span> to execute and capture the conversation.
      </div>
    )
  }
  if (loading && messages.length === 0) {
    return <div className="p-6 text-center text-sm text-app-muted">Loading conversation…</div>
  }
  // ScenarioDetail always renders a recorded transcript between a
  // simulated red-team user and the agent — not a live "you" chat.
  // Keep role icons, but force clear speaker names in the label row.
  const userLabel = "User"
  const assistantLabel = run?.agentName?.trim() || "Agent"
  const inlineWorkflowRows = run?.workflowTrace ? workflowInlineRows(run.workflowTrace) : []
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.85fr)]">
      <div className="min-w-0 space-y-1.5">
        {/* "chat" mode renders Forge in its own sibling tab now (see
            ScenarioDetail's tabs row) — the Conversation tab stays just
            the transcript so failure context and recorded turns are
            visually separate. "callout" mode (other surfaces) still gets
            the static yellow Fix-with-Forge banner here. */}
        {failed && failurePaneMode === "callout" && (
          <ForgeOnFailure judgeSummary={run?.summary ?? scenario.latestRunSummary} onFix={onFix} />
        )}
        {messages.length === 0 ? (
          <div className="rounded-card border border-border bg-app-bg p-4 text-sm text-app-muted">
            No transcript available for this run.
          </div>
        ) : (
          conversationItems(messages, inlineWorkflowRows).map((item) => (
            item.kind === "message" ? (
              <TestConversationMessageCard
                key={`msg-${item.message.id}`}
                msg={item.message}
                userLabel={userLabel}
                assistantLabel={assistantLabel}
              />
            ) : (
              <InlineWorkflowEvent key={item.row.key} row={item.row} />
            )
          ))
        )}
      </div>

      <div className="min-w-0">
        <ConversationTraceAside
          trace={run?.workflowTrace ?? null}
          finalState={run?.finalState ?? null}
          appuserContext={run?.appuserContext ?? null}
          modelRequests={run?.modelRequests ?? null}
          loading={loading}
        />
      </div>
    </div>
  )
}

function ConversationTraceAside({
  trace,
  finalState,
  appuserContext,
  modelRequests,
  loading,
}: {
  trace: WorkflowTracePayload | null
  finalState: TestRunFinalState | null
  appuserContext: TestRunAppuserContext | null
  modelRequests: TestRunModelRequestSummary | null
  loading: boolean
}) {
  if (loading && !trace && !finalState) {
    return (
      <aside className="rounded-card border border-border bg-app-bg p-3 text-sm text-app-muted">
        Loading workflow context...
      </aside>
    )
  }
  if (!trace && !finalState && !appuserContext) {
    return (
      <aside className="rounded-card border border-border bg-app-bg p-3 text-sm text-app-muted">
        No workflow context was recorded for this run.
      </aside>
    )
  }

  if (!trace) {
    return (
      <aside className="space-y-3 xl:sticky xl:top-3">
        <RunOutcomeCard finalState={finalState} modelRequests={modelRequests} />
        <AppuserContextCard context={appuserContext} />
        <div className="rounded-card border border-border bg-app-bg p-3 text-sm text-app-muted">
          No workflow context was recorded for this run.
        </div>
      </aside>
    )
  }

  const context = latestAllyContext(trace)
  const contract = asRecord(context?.workflow_contract)
  const status = asRecord(contract.workflow_status)
  const skillCard = activeSkillCardSummary(trace)
  const fields = pickContextRecord(asRecord(skillCard.fields), status.fields, contract.fields)
  const files = pickContextRecord(asRecord(skillCard.files), status.files, contract.files)
  const markers = pickContextRecord(asRecord(skillCard.markers), status.markers, contract.markers)
  const dataLoads = pickContextRecord(asRecord(skillCard.data), status.data, contract.data)
  const policies = pickContextRecord(asRecord(skillCard.policies), status.policies, contract.policy_results)
  const completedActions = pickContextRecord(asRecord(skillCard.actions), status.completed_actions, arrayToStatusRecord(contract.completed_actions))
  const pendingActions = Array.isArray(status.pending_actions) ? status.pending_actions.map(String) : []
  const missing = Array.isArray(status.missing_requirements) ? status.missing_requirements : Array.isArray(contract.missing_requirements) ? contract.missing_requirements : []
  const response = asRecord(contract.response)
  const nextBestAction = nextBestActionSummary(response.next_best_action)
  const usefulEvents = usefulConversationTraceEvents(trace).slice(-12).reverse()
  const retryContexts = allyRetryContexts(trace).slice(-4).reverse()
  const skillLabel = stringish(asRecord(contract.skill).key) || stringish(contract.skill_key) || skillCardForeground(trace) || "Workflow packet"
  const contextStatus = stringish(contract.status) || stringish(status.status) || skillCardStatus(trace) || "unknown"

  return (
    <aside className="space-y-3 xl:sticky xl:top-3">
      <RunOutcomeCard finalState={finalState} modelRequests={modelRequests} />
      <AppuserContextCard context={appuserContext} />

      <div className="rounded-card border border-border bg-app-bg p-3">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-app-muted">
              <Info size={13} />
              Ally context
            </div>
            <div className="mt-1 text-sm font-medium text-app-text">
              {skillLabel}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-1">
            <TraceChip label="turn" value={context?.turn_number == null ? "n/a" : String(context.turn_number)} />
            <TraceChip label="status" value={contextStatus} />
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <MiniContextSection title="Fields" empty="No fields">
            {Object.entries(fields).map(([key, value]) => (
              <ContextMiniRow key={key} label={key} value={fieldContextLabel(value)} detail={fieldContextState(value)} raw={value} />
            ))}
          </MiniContextSection>

          <MiniContextSection title="Files" empty="No files">
            {Object.entries(files).map(([key, value]) => (
              <ContextMiniRow key={key} label={key} value={fileContextLabel(value)} detail={fileContextStatus(value)} raw={value} />
            ))}
          </MiniContextSection>

          <MiniContextSection title="Markers" empty="No markers">
            {Object.entries(markers).map(([key, value]) => (
              <ContextMiniRow key={key} label={key} value={markerContextStatus(value)} detail={markerContextDetail(value)} raw={value} />
            ))}
          </MiniContextSection>

          <MiniContextSection title="Data" empty="No data loaded">
            {Object.entries(dataLoads).map(([key, value]) => (
              <ContextMiniRow key={key} label={key} value={dataContextStatus(value)} detail={dataContextEndpoint(value)} raw={value} />
            ))}
          </MiniContextSection>

          <MiniContextSection title="Policies" empty="No policies">
            {Object.entries(policies).map(([key, value]) => (
              <ContextMiniRow key={key} label={key} value={policyContextVerdict(value)} raw={value} />
            ))}
          </MiniContextSection>

          <MiniContextSection title="Actions" empty="No completed actions">
            {Object.entries(completedActions).map(([key, value]) => (
              <ContextMiniRow key={key} label={key} value={actionContextStatus(value)} detail={actionContextType(value)} raw={value} />
            ))}
            {pendingActions.map((action) => (
              <ContextMiniRow key={`pending-${action}`} label={action} value="pending" />
            ))}
          </MiniContextSection>
        </div>

        {missing.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-app-muted">Missing</div>
            <div className="flex flex-wrap gap-1">
              {missing.map((row, index) => (
                <span key={`${missingRequirementLabel(row)}-${index}`} className="rounded-control border border-status-warning/30 bg-status-warning/10 px-2 py-1 text-[11px] text-status-warning">
                  {missingRequirementLabel(row)}
                </span>
              ))}
            </div>
          </div>
        )}

        {nextBestAction && (
          <details className="mt-3 rounded-control border border-border bg-app-panel/60 p-2 text-xs">
            <summary className="cursor-pointer list-none text-app-text">Next best action</summary>
            <p className="mt-2 whitespace-pre-wrap text-app-muted">{nextBestAction}</p>
          </details>
        )}

        {retryContexts.length > 0 && (
          <details className="mt-3 rounded-control border border-status-warning/30 bg-status-warning/10 p-2 text-xs" open>
            <summary className="cursor-pointer list-none text-status-warning">[retried] Ally context</summary>
            <div className="mt-2 space-y-2">
              {retryContexts.map((row) => (
                <RetryContextRow key={`${row.seq}-${row.label || "retry"}`} context={row} />
              ))}
            </div>
          </details>
        )}

        <details className="mt-3 rounded-control border border-border bg-app-panel/60 p-2 text-xs">
          <summary className="cursor-pointer list-none text-app-text">Prompt packet JSON</summary>
          <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-app-bg p-2 text-[10px] text-app-muted">
            {JSON.stringify(compactAllyContextForDisplay(context), null, 2)}
          </pre>
        </details>
      </div>

      <details className="rounded-card border border-border bg-app-bg p-3" open>
        <summary className="cursor-pointer list-none text-sm font-medium text-app-text">Useful trace</summary>
        <div className="mt-3 space-y-1.5">
          {usefulEvents.length === 0 ? (
            <div className="text-xs text-app-muted">No useful trace events recorded.</div>
          ) : (
            usefulEvents.map((event) => <CompactTraceEvent key={`${event.seq}-${event.event}`} event={event} />)
          )}
        </div>
      </details>

      {modelRequests && (
        <details className="rounded-card border border-border bg-app-bg p-3">
          <summary className="cursor-pointer list-none text-sm font-medium text-app-text">Model calls</summary>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <TraceChip label="calls" value={String(modelRequests.totalRequests)} />
            <TraceChip label="input" value={String(modelRequests.inputTokens)} />
            <TraceChip label="output" value={String(modelRequests.outputTokens)} />
            <TraceChip label="cost" value={`$${modelRequests.costUsd.toFixed(4)}`} />
          </div>
        </details>
      )}
    </aside>
  )
}

function RunOutcomeCard({ finalState, modelRequests }: { finalState: TestRunFinalState | null; modelRequests: TestRunModelRequestSummary | null }) {
  if (!finalState && !modelRequests) {
    return (
      <div className="rounded-card border border-border bg-app-bg p-3 text-sm text-app-muted">
        No final conversation state was recorded.
      </div>
    )
  }

  const state = finalState?.state || "unknown"
  const ratingStatus = finalState ? ratingPromptStatus(finalState) : null
  const feedbackCount = Number(finalState?.feedbackSubmittedCount || 0)
  const agentGroups = (modelRequests?.byAgent ?? []).slice(0, 6)
  const totalModelHits = Number(modelRequests?.totalRequests || 0)

  return (
    <div className="rounded-card border border-border bg-app-bg p-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-app-muted">
            <CheckCircle2 size={13} />
            Run outcome
          </div>
          <div className="mt-1 text-sm font-medium text-app-text">{formatStateLabel(state)}</div>
        </div>
        <span className={cn("rounded-chip border px-2 py-1 text-[11px] font-medium", outcomeStateClass(state))}>
          {state}
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
        <OutcomeMiniRow label="Final state" value={finalState ? formatStateLabel(state) : "Not recorded"} detail={finalState ? terminalStateDetail(finalState) : null} />
        {modelRequests && <OutcomeMiniRow label="Model cost" value={formatUsd(modelRequests.costUsd)} detail={`${totalModelHits} hit${totalModelHits === 1 ? "" : "s"}`} />}
        {agentGroups.length > 0 && <OutcomeMiniRow label="Agent hits" value={agentGroups.map((group) => `${group.label} ${group.requestCount}`).join(", ")} />}
        {ratingStatus && <OutcomeMiniRow label="Feedback/rating" value={ratingStatus.value} detail={ratingStatus.detail} />}
        {feedbackCount > 0 && <OutcomeMiniRow label="Feedback saved" value={String(feedbackCount)} detail={feedbackCount === 1 ? "report" : "reports"} />}
        {finalState?.aiUserRating && <OutcomeMiniRow label="AI rating" value={finalState.aiUserRating} />}
        {finalState?.humanUserRating && <OutcomeMiniRow label="Human rating" value={finalState.humanUserRating} />}
      </div>

      {agentGroups.length > 0 && (
        <details className="mt-3 rounded-control border border-border bg-app-panel/60 p-2 text-xs">
          <summary className="cursor-pointer list-none text-app-text">Agent/model hits</summary>
          <div className="mt-2 space-y-2">
            {agentGroups.map((group) => (
              <div key={group.kind} className="min-w-0 rounded-control border border-border bg-app-bg/80 p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-app-text">{group.label}</span>
                  <code className="shrink-0 text-app-muted">{group.requestCount} hit{group.requestCount === 1 ? "" : "s"} · {formatUsd(group.costUsd)}</code>
                </div>
                <div className="mt-1 truncate text-[11px] text-app-muted">
                  {group.inputTokens} in / {group.outputTokens} out · {compactCounts(group.models)}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {finalState?.escalationReason && (
        <details className="mt-3 rounded-control border border-border bg-app-panel/60 p-2 text-xs">
          <summary className="cursor-pointer list-none text-app-text">Escalation reason</summary>
          <p className="mt-2 whitespace-pre-wrap text-app-muted">{finalState.escalationReason}</p>
        </details>
      )}
    </div>
  )
}

function AppuserContextCard({ context }: { context: TestRunAppuserContext | null }) {
  if (!context) return null

  const customFields = asRecord(context.customFields)
  const overrides = asRecord(context.testAppuserOverrides)
  const identityRows: Array<[string, unknown]> = []
  const displayName = context.displayName || [context.firstName, context.lastName].filter(Boolean).join(" ")
  const rawIdentityRows: Array<[string, unknown]> = [
    ["source_key", context.sourceKey],
    ["state", context.state],
    ["name", displayName],
    ["email", context.email],
    ["external_id", context.externalId],
  ]
  rawIdentityRows.forEach(([key, value]) => {
    if (value != null && String(value).trim() !== "") identityRows.push([String(key), value])
  })
  const customFieldCount = Object.keys(customFields).length

  return (
    <div className="rounded-card border border-border bg-app-bg p-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-app-muted">
            <Database size={13} />
            Test user
          </div>
          <div className="mt-1 text-sm font-medium text-app-text">
            {context.displayName || context.email || context.sourceKey}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <TraceChip label="id" value={String(context.id)} />
          <TraceChip label="fields" value={String(customFieldCount)} />
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
        <MiniContextSection title="Identity" empty="No identity data">
          {identityRows.map(([key, value]) => (
            <ContextMiniRow key={key} label={key} value={shortCollectedValue(value)} raw={value} />
          ))}
        </MiniContextSection>

        <MiniContextSection title="Custom fields" empty="No custom fields">
          {Object.entries(customFields).map(([key, value]) => (
            <ContextMiniRow key={key} label={key} value={shortCollectedValue(value)} detail={valueKind(value)} raw={value} />
          ))}
        </MiniContextSection>
      </div>

      {Object.keys(overrides).length > 0 && (
        <details className="mt-3 rounded-control border border-border bg-app-panel/60 p-2 text-xs">
          <summary className="cursor-pointer list-none text-app-text">Scenario appuser overrides</summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-app-bg p-2 text-[10px] text-app-muted">
            {JSON.stringify(overrides, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )
}

function OutcomeMiniRow({ label, value, detail }: { label: string; value: string | null; detail?: string | null }) {
  return (
    <div className="min-w-0 rounded-control border border-border bg-app-panel/50 p-2 text-[11px]">
      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">{label}</div>
      <div className="flex min-w-0 items-center gap-1 text-app-text">
        {value && <code className="truncate">{value}</code>}
        {detail && <span className="shrink-0 rounded-chip border border-border bg-white/5 px-1 py-0.5 text-app-muted">{detail}</span>}
      </div>
    </div>
  )
}

function ratingPromptStatus(finalState: TestRunFinalState): { value: string; detail?: string | null } {
  if (finalState.badRatingFeedbackActive) return { value: "bad-rating follow-up", detail: "waiting" }
  if (finalState.ratingPromptPostedAt) return { value: "prompt posted", detail: timeish(finalState.ratingPromptPostedAt) }
  if (finalState.state === "pending_feedback") return { value: "pending feedback", detail: "waiting" }
  if (finalState.completionFollowupActive) return { value: "would ask after close", detail: "follow-up active" }
  if (finalState.aiUserRating || finalState.humanUserRating) return { value: "rated" }
  return { value: "not triggered" }
}

function terminalStateDetail(finalState: TestRunFinalState): string | null {
  if (finalState.escalatedAt) return timeish(finalState.escalatedAt)
  if (finalState.resolvedAt) return timeish(finalState.resolvedAt)
  return null
}

function timeish(value: string | null | undefined): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

function formatStateLabel(state: string): string {
  return state.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ")
}

function outcomeStateClass(state: string): string {
  if (state === "escalated") return "border-status-warning/40 bg-status-warning/10 text-status-warning"
  if (state === "resolved" || state === "auto_resolved" || state === "assumed_resolved") return "border-status-success/40 bg-status-success/10 text-status-success"
  if (state === "pending_feedback") return "border-brand-red/40 bg-brand-red/10 text-brand-red"
  if (state === "rage_locked" || state === "attempted_hack") return "border-status-error/40 bg-status-error/10 text-status-error"
  return "border-border bg-white/[0.04] text-app-muted"
}

function MiniContextSection({ title, empty, children }: { title: string; empty: string; children: ReactNode }) {
  const items = Array.isArray(children) ? children.filter(Boolean) : children ? [children] : []
  return (
    <div className="min-w-0 rounded-control border border-border bg-app-panel/50 p-2">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-app-muted">{title}</div>
      {items.length === 0 ? (
        <div className="text-[11px] text-app-muted">{empty}</div>
      ) : (
        <div className="space-y-1">{children}</div>
      )}
    </div>
  )
}

function ContextMiniRow({ label, value, detail, raw }: { label: string; value: string | null; detail?: string | null; raw?: unknown }) {
  const title = raw == null ? undefined : hoverJson(raw)
  return (
    <div className="min-w-0 text-[11px]" title={title}>
      <div className="truncate text-app-text">{label}</div>
      <div className="flex min-w-0 items-center gap-1 text-app-muted">
        {value && <code className="truncate">{value}</code>}
        {detail && <span className="shrink-0 rounded-chip border border-border bg-white/5 px-1 py-0.5">{detail}</span>}
      </div>
    </div>
  )
}

function RetryContextRow({ context }: { context: WorkflowAllyContext }) {
  const retry = asRecord(context.retry)
  const changes = Array.isArray(retry.changes) ? retry.changes : []
  const visibleChanges = changes.slice(0, 4)
  const label = stringish(context.label) || `[retried] ${stringish(retry.kind) || "workflow"}`
  const detail = retryTraceDetail(retry)

  return (
    <details className="rounded-control border border-status-warning/30 bg-app-bg/70 p-2">
      <summary className="flex cursor-pointer list-none items-center gap-2">
        <span className="min-w-0 truncate text-status-warning">{label}</span>
        {detail && <code className="ml-auto max-w-[45%] truncate text-app-muted">{detail}</code>}
      </summary>
      {visibleChanges.length > 0 && (
        <div className="mt-2 space-y-1">
          {visibleChanges.map((change, index) => {
            const row = asRecord(change)
            return (
              <div key={`${stringish(row.path) || "change"}-${index}`} className="min-w-0 rounded-control border border-border bg-app-panel/50 p-1.5 text-[11px]">
                <div className="truncate text-app-text">{stringish(row.path) || "change"}</div>
                <div className="truncate text-app-muted">
                  {shortCollectedValue(row.before)} → {shortCollectedValue(row.after)}
                </div>
              </div>
            )
          })}
        </div>
      )}
      <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-app-bg p-2 text-[10px] text-app-muted">
        {JSON.stringify(retry, null, 2)}
      </pre>
    </details>
  )
}

function CompactTraceEvent({ event }: { event: WorkflowTraceEvent }) {
  const details = asRecord(event.details)
  const title = compactTraceTitle(event)
  const detail = compactTraceDetail(event)

  return (
    <details className="rounded-control border border-border bg-app-panel/50 px-2 py-1 text-xs">
      <summary className="flex cursor-pointer list-none items-center gap-2">
        <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] text-app-muted">
          t{event.turn_number ?? "?"}
        </span>
        <span className="min-w-0 truncate text-app-text">{title}</span>
        {detail && <code className="ml-auto max-w-[45%] truncate text-app-muted">{detail}</code>}
      </summary>
      <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-app-bg p-2 text-[10px] text-app-muted">
        {JSON.stringify(details, null, 2)}
      </pre>
    </details>
  )
}

function latestAllyContext(trace: WorkflowTracePayload): WorkflowAllyContext | null {
  const rows = Array.isArray(trace.allyContexts) ? trace.allyContexts : []
  const contextRow = [...rows].reverse().find((row) => Object.keys(asRecord(row.workflow_contract)).length > 0)
  return contextRow ?? fallbackAllyContext(trace)
}

function allyRetryContexts(trace: WorkflowTracePayload): WorkflowAllyContext[] {
  return (Array.isArray(trace.allyContexts) ? trace.allyContexts : []).filter((row) => Object.keys(asRecord(row.retry)).length > 0)
}

function fallbackAllyContext(trace: WorkflowTracePayload): WorkflowAllyContext | null {
  const events = Array.isArray(trace.trace) ? trace.trace : []
  const event = [...events].reverse().find((row) => {
    const details = asRecord(row.details)
    return Object.keys(asRecord(details.workflow_contract)).length > 0 ||
      Object.keys(asRecord(details.contract)).length > 0
  })
  if (!event) return null

  const details = asRecord(event.details)
  const workflowContract = asRecord(details.workflow_contract)
  const contract = Object.keys(workflowContract).length > 0 ? workflowContract : asRecord(details.contract)
  return {
    seq: event.seq,
    at: event.at,
    turn_number: event.turn_number,
    workflow_contract: contract,
  }
}

function compactAllyContextForDisplay(context: WorkflowAllyContext | null): Record<string, unknown> {
  if (!context) return {}

  const contract = asRecord(context.workflow_contract)
  return {
    label: context.label,
    model_role: context.model_role,
    turn_number: context.turn_number,
    hop: context.hop,
    tool_keys: context.tool_keys,
    tool_count: context.tool_count,
    history_count: context.history_count,
    ally_context: context.ally_context,
    workflow_contract: contract,
  }
}

function usefulConversationTraceEvents(trace: WorkflowTracePayload): WorkflowTraceEvent[] {
  const useful = new Set([
    "action_completed",
    "action_failed",
    "field_committed",
    "field_invalidated",
    "human_turn",
    "response_next_best_action",
    "workflow_v3_planner",
    "workflow_v3_planner_retried",
    "workflow_v3_reply",
    "workflow_v3_reply_invalid",
    "workflow_v3_reply_retried",
    "workflow_v3_state",
    "workflow_error",
    "dropped_unavailable_tool_calls",
  ])
  return (Array.isArray(trace.trace) ? trace.trace : []).filter((event) => useful.has(event.event))
}

function arrayToStatusRecord(value: unknown): Record<string, unknown> {
  if (!Array.isArray(value)) return {}
  return value.reduce<Record<string, unknown>>((memo, item) => {
    memo[String(item)] = { status: "completed" }
    return memo
  }, {})
}

function pickContextRecord(...values: unknown[]): Record<string, unknown> {
  for (const value of values) {
    const record = asRecord(value)
    if (Object.keys(record).length > 0) return record
  }
  return {}
}

function fieldContextLabel(value: unknown): string | null {
  const row = asRecord(value)
  return stringish(row.label) || stringish(row.name) || stringish(row.value) || stringish(row.id)
}

function fieldContextState(value: unknown): string | null {
  const row = asRecord(value)
  return stringish(asRecord(row.valet_meta).state) || stringish(row.state)
}

function fileContextLabel(value: unknown): string | null {
  const row = asRecord(value)
  return stringish(row.filename) || stringish(row.content_type)
}

function fileContextStatus(value: unknown): string | null {
  return stringish(asRecord(value).status)
}

function markerContextStatus(value: unknown): string | null {
  const row = asRecord(value)
  if (row.set === true) return "set"
  if (row.set === false) return "unset"
  return shortCollectedValue(value)
}

function markerContextDetail(value: unknown): string | null {
  const row = asRecord(value)
  return stringish(row.turn_number) || timeish(stringish(row.set_at))
}

function dataContextStatus(value: unknown): string | null {
  const row = asRecord(value)
  return stringish(row.status) || shortCollectedValue(row.result ?? value)
}

function dataContextEndpoint(value: unknown): string | null {
  return stringish(asRecord(value).endpoint)
}

function policyContextVerdict(value: unknown): string | null {
  return stringish(asRecord(value).verdict) || shortCollectedValue(value)
}

function actionContextStatus(value: unknown): string | null {
  return stringish(asRecord(value).status) || shortCollectedValue(value)
}

function actionContextType(value: unknown): string | null {
  const row = asRecord(value)
  return stringish(row.type) || stringish(row.endpoint) || stringish(row.tool)
}

function nextBestActionSummary(value: unknown): string | null {
  const row = asRecord(value)
  if (Object.keys(row).length > 0) {
    const doText = stringish(row.do)
    const doNot = Array.isArray(row.do_not) ? row.do_not.map(String).filter(Boolean) : []
    return [doText && `Do: ${doText}`, doNot.length > 0 && `Do not: ${doNot.join("; ")}`].filter(Boolean).join("\n") || null
  }

  return stringish(value)
}

function missingRequirementLabel(value: unknown): string {
  const row = asRecord(value)
  return stringish(row.key) || stringish(row.field) || stringish(row.file) || stringish(row.action) || stringish(row.kind) || "requirement"
}

function compactTraceTitle(event: WorkflowTraceEvent): string {
  const details = asRecord(event.details)
  if (event.event === "human_turn") return "human turn"
  if (event.event === "response_next_best_action") return "next best action"
  if (event.event === "workflow_v3_planner") return "planner"
  if (event.event === "workflow_v3_planner_retried") return "[retried] planner"
  if (event.event === "workflow_v3_reply") return "reply"
  if (event.event === "workflow_v3_reply_invalid") return "reply invalid"
  if (event.event === "workflow_v3_reply_retried") return "[retried] reply"
  if (event.event === "workflow_v3_state") return `workflow ${stringish(details.foreground) || "state"}`
  if (event.event === "field_committed") return `save ${stringish(details.field) || "field"}`
  if (event.event === "field_invalidated") return `reject ${stringish(details.field) || "field"}`
  if (stringish(details.owner) === "workflow_v3_data" && stringish(details.endpoint)) return `fetch ${stringish(details.endpoint)}`
  if (stringish(details.endpoint)) return `endpoint ${stringish(details.endpoint)}`
  if (stringish(details.policy)) return `policy ${stringish(details.policy)}`
  if (stringish(details.action_key)) return `action ${stringish(details.action_key)}`
  return formatTraceEvent(event.event)
}

function compactTraceDetail(event: WorkflowTraceEvent): string | null {
  const details = asRecord(event.details)
  if (event.event === "human_turn") return stringish(details.content_preview)
  if (event.event === "response_next_best_action") return nextBestActionSummary(asRecord(details.next_best_action))
  if (event.event === "workflow_v3_planner") return shortCollectedValue(details.result_data)
  if (event.event === "workflow_v3_planner_retried") return retryTraceDetail(details.result_data)
  if (event.event === "workflow_v3_reply" || event.event === "workflow_v3_reply_invalid") return shortCollectedValue(details.result_data)
  if (event.event === "workflow_v3_reply_retried") return retryTraceDetail(details.result_data)
  if (event.event === "workflow_v3_state") {
    const active = Array.isArray(details.active_skill_keys) ? details.active_skill_keys.join(", ") : ""
    const paused = Array.isArray(details.paused_skill_keys) ? details.paused_skill_keys.join(", ") : ""
    const inactive = Array.isArray(details.inactive_skill_keys) ? details.inactive_skill_keys.join(", ") : ""
    return [`active: ${active || "none"}`, paused && `paused: ${paused}`, inactive && `inactive: ${inactive}`].filter(Boolean).join(" | ")
  }
  if (event.event === "field_committed") return [stringish(details.field_state), stringish(details.label)].filter(Boolean).join(" ") || null
  if (event.event === "field_invalidated") return stringish(details.reason)
  const result = asRecord(details.result)
  return stringish(result.status) || stringish(details.owner)
}

function retryTraceDetail(value: unknown): string | null {
  const retry = asRecord(value)
  const from = stringish(retry.from_attempt)
  const to = stringish(retry.to_attempt)
  const changes = Array.isArray(retry.changes) ? retry.changes.length : 0
  const attemptText = from && to ? `attempt ${from} → ${to}` : null
  const changeText = changes > 0 ? `${changes} change${changes === 1 ? "" : "s"}` : null
  return [attemptText, changeText].filter(Boolean).join(" · ") || stringish(retry.status)
}

function AssertionsTab({ verdicts, hasRun }: { verdicts: TestLabAssertionVerdict[]; hasRun: boolean }) {
  if (!hasRun) {
    return <div className="text-sm text-app-muted">Assertions populate after the first run.</div>
  }
  if (verdicts.length === 0) {
    return <div className="text-sm text-app-muted">No assertions on this scenario.</div>
  }
  return (
    <ol className="space-y-3">
      {verdicts.map((v, i) => (
        <li key={`${v.id}-${i}`} className="rounded-card border border-border bg-app-bg p-3">
          <div className="flex items-start gap-2">
            {v.passed ? (
              <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-status-success" />
            ) : (
              <XCircle size={16} className="mt-0.5 shrink-0 text-status-error" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="rounded-control border border-border bg-app-panel px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-app-muted">
                  {v.kind === "failure" ? "must NOT" : "must"}
                </span>
                <span className="text-sm text-app-text">{v.text}</span>
              </div>
              {v.reason && (
                <p className="mt-1.5 text-xs italic text-app-muted">{v.reason}</p>
              )}
            </div>
          </div>
        </li>
      ))}
    </ol>
  )
}

function WorkflowRunSummary({ trace, compact = false }: { trace: WorkflowTracePayload; compact?: boolean }) {
  const collectedRows = collectedDataRows(trace)
  const actionRows = workflowActionRows(trace).slice(-10).reverse()

  if (collectedRows.length === 0 && actionRows.length === 0) return null

  return (
    <div className={cn(
      "rounded-card border border-border bg-app-bg p-3",
      compact && "text-xs",
    )}>
      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-app-muted">
            <Database size={13} />
            Collected data
          </div>
          {collectedRows.length === 0 ? (
            <div className="text-xs text-app-muted">No collected data recorded.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {collectedRows.map((row) => (
                <DataChip key={row.key} label={row.label} value={row.value} meta={row.meta} />
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-app-muted">
            <Wrench size={13} />
            Workflow activity
          </div>
          {actionRows.length === 0 ? (
            <div className="text-xs text-app-muted">No workflow activity recorded.</div>
          ) : (
            <div className="space-y-1.5">
              {actionRows.map((row) => (
                <WorkflowActionChip key={row.key} row={row} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type CollectedDataMeta = {
  source?: string | null
  replaced?: {
    value?: unknown
    source?: string | null
  } | null
}

function DataChip({ label, value, meta }: { label: string; value: string; meta?: CollectedDataMeta }) {
  const source = meta?.source
  const replaced = meta?.replaced
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-control border border-border bg-app-panel px-2 py-1 text-xs">
      <span className="shrink-0 text-app-muted">{label}</span>
      <code className="truncate text-app-text">{value}</code>
      {source && (
        <span className="shrink-0 rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] text-app-muted">
          {source}
        </span>
      )}
      {replaced && replaced.value != null && (
        <span className="shrink-0 rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] text-app-muted">
          replaced {replaced.source || "previous"}: {shortCollectedValue(replaced.value)}
        </span>
      )}
    </span>
  )
}

type WorkflowActionRow = {
  key: string
  label: string
  detail: string | null
  status: "ok" | "waiting" | "blocked" | "error"
  turnNumber?: number | null
  actionKey?: string | null
  actionType?: string | null
  owner?: string | null
  endpoint?: string | null
  tool?: string | null
  payload?: unknown
}

type ConversationItem =
  | { kind: "message"; message: ChatBubble }
  | { kind: "workflow"; row: WorkflowActionRow }

function WorkflowActionChip({ row }: { row: WorkflowActionRow }) {
  const tone =
    row.status === "error" ? "border-status-error/35 bg-status-error/10 text-status-error"
    : row.status === "waiting" || row.status === "blocked" ? "border-status-warning/35 bg-status-warning/10 text-status-warning"
    : "border-status-success/30 bg-status-success/10 text-status-success"

  return (
    <details className={cn("rounded-control border px-2 py-1 text-xs", tone)}>
      <summary className="cursor-pointer list-none">
        <span className="font-semibold">{workflowRowStatusLabel(row)}</span>
        <span className="ml-1 text-app-text">{row.label}</span>
        {row.detail && <code className="ml-1 text-app-muted">{row.detail}</code>}
      </summary>
      {row.payload != null && (
        <pre className="mt-1.5 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-app-panel p-2 text-[10px] text-app-muted">
          {JSON.stringify(row.payload, null, 2)}
        </pre>
      )}
    </details>
  )
}

function workflowRowStatusLabel(row: WorkflowActionRow): string {
  if (row.status === "error") return "failed"
  if (row.status === "waiting") return "waiting"
  if (row.status === "blocked") return "blocked"
  if (row.actionType === "field") return "saved"
  if (row.owner === "skill_card_data") return "fetched"
  if (row.actionType === "policy") return "evaluated"
  if (row.owner === "tool_dispatch") return "tool"
  return "completed"
}

function InlineWorkflowEvent({ row }: { row: WorkflowActionRow }) {
  const isError = row.status === "error"
  const tone = isError
    ? "border-status-error/30 bg-status-error/[0.06] text-status-error"
    : "border-status-success/20 bg-status-success/[0.04] text-status-success"
  const Icon = isError ? X : CheckCircle2

  return (
    <div className="flex w-full gap-3 pl-[4.25rem]">
      <details className={cn("max-w-[760px] rounded-control border px-3 py-2 text-xs", tone)}>
        <summary className="flex cursor-pointer list-none items-center gap-2">
          <Icon size={13} />
          <span className="font-semibold">{workflowRowStatusLabel(row)}</span>
          <span className="text-app-text">{row.label}</span>
          {row.detail && <code className="text-app-muted">{row.detail}</code>}
        </summary>
        {row.payload != null && (
          <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-app-panel p-2 text-[10px] text-app-muted">
            {JSON.stringify(row.payload, null, 2)}
          </pre>
        )}
      </details>
    </div>
  )
}

function WorkflowTraceTab({ trace, loading, hasRun }:
  { trace: WorkflowTracePayload | null; loading: boolean; hasRun: boolean }) {
  if (!hasRun) {
    return <div className="text-sm text-app-muted">Workflow trace populates after the first run.</div>
  }
  if (loading && !trace) {
    return <div className="text-sm text-app-muted">Loading workflow trace...</div>
  }
  if (!trace) {
    return <div className="text-sm text-app-muted">No workflow trace was recorded for this run.</div>
  }

  const events = Array.isArray(trace.trace) ? trace.trace : []
  const state = trace.state ?? {}
  const skillCard = activeSkillCardSummary(trace)
  const isSkillCard = isSkillCardTrace(trace)
  const latestWithRequirements = [...events].reverse().find((e) => Array.isArray(e.details?.requirements) && e.details.requirements.length > 0)
  const latestRequirements = latestWithRequirements?.details.requirements ?? []
  const currentCard = String(state.active_card_id ?? latestWithRequirements?.details.active_card_id ?? "")
  const currentStatus = String(state.card_status ?? latestWithRequirements?.details.card_status ?? "")

  return (
    <div className="space-y-4">
      {isSkillCard && (
        <SkillCardReplayPanel trace={trace} />
      )}

      <div className="rounded-card border border-border bg-app-bg p-3">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {isSkillCard ? (
            <>
              <TraceChip label="engine" value="workflow_v3" />
              <TraceChip label="foreground" value={skillCardForeground(trace) || "none"} />
              <TraceChip label="status" value={skillCardStatus(trace) || "unknown"} />
              {skillCard.skill_version_id != null && <TraceChip label="version" value={String(skillCard.skill_version_id)} />}
            </>
          ) : (
            <>
              <TraceChip label="status" value={currentStatus || "unknown"} />
              <TraceChip label="card" value={currentCard || "none"} />
              {state.active_skill_version_id != null && <TraceChip label="version" value={String(state.active_skill_version_id)} />}
            </>
          )}
          <span className="ml-auto text-xs text-app-muted">{events.length} trace events</span>
        </div>

        {isSkillCard && (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <TraceChip label="fields" value={String(Object.keys(asRecord(skillCard.fields)).length)} />
            <TraceChip label="data" value={String(Object.keys(asRecord(skillCard.data)).length)} />
            <TraceChip label="policies" value={String(Object.keys(asRecord(skillCard.policies)).length)} />
            <TraceChip label="actions" value={String(Object.keys(asRecord(skillCard.actions)).length)} />
          </div>
        )}

        {!isSkillCard && latestRequirements.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-app-muted">
              <Lock size={13} />
              Current locks and requirements
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {latestRequirements.map((req, i) => (
                <RequirementPill key={`${requirementLabel(req)}-${i}`} req={req} />
              ))}
            </div>
          </div>
        )}
      </div>

      <WorkflowRunSummary trace={trace} />

      <details className="rounded-card border border-border bg-app-bg p-3">
        <summary className="cursor-pointer text-sm font-medium text-app-text">Raw workflow debug JSON</summary>
        <pre className="mt-3 max-h-[460px] overflow-auto whitespace-pre-wrap break-all rounded-control border border-border bg-app-panel p-3 text-[11px] text-app-muted">
          {JSON.stringify(trace, null, 2)}
        </pre>
      </details>
    </div>
  )
}

function SkillCardReplayPanel({ trace }: { trace: WorkflowTracePayload }) {
  const cards = skillCardCards(trace)

  return (
    <div className="rounded-card border border-border bg-app-bg p-3">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-app-text">Workflow replay</div>
          <p className="mt-1 text-xs text-app-muted">
            Shows planner decisions, loaded data, saved fields, policy results, actions, and replies for WorkflowV3.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          <TraceChip label="foreground" value={skillCardForeground(trace) || "none"} />
          <TraceChip label="active" value={String(cards.filter(([, card]) => !inactiveSkillCardStatus(card)).length)} />
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="text-xs text-app-muted">No Score Card state was saved.</div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {cards.map(([key, card]) => (
            <div key={key} className="rounded-control border border-border bg-app-panel/50 p-3">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-app-text">
                    {stringish(card.skill_name) || key}
                  </div>
                  <code className="text-xs text-app-muted">{key}</code>
                </div>
                <span className="rounded-chip border border-border bg-white/5 px-2 py-1 text-[11px] text-app-muted">
                  {stringish(card.status) || "unknown"}
                </span>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <MiniContextSection title="Fields" empty="No fields">
                  {Object.entries(asRecord(card.fields)).map(([fieldKey, value]) => (
                    <ContextMiniRow key={fieldKey} label={fieldKey} value={fieldContextLabel(value)} detail={fieldContextState(value)} raw={value} />
                  ))}
                </MiniContextSection>

                <MiniContextSection title="Data" empty="No data loaded">
                  {Object.entries(asRecord(card.data)).map(([dataKey, value]) => (
                    <ContextMiniRow key={dataKey} label={dataKey} value={dataContextStatus(value)} detail={dataContextEndpoint(value)} raw={value} />
                  ))}
                </MiniContextSection>

                <MiniContextSection title="Policies" empty="No policies">
                  {Object.entries(asRecord(card.policies)).map(([policyKey, value]) => (
                    <ContextMiniRow key={policyKey} label={policyKey} value={policyContextVerdict(value)} raw={value} />
                  ))}
                </MiniContextSection>

                <MiniContextSection title="Actions" empty="No actions">
                  {Object.entries(asRecord(card.actions)).map(([actionKey, value]) => (
                    <ContextMiniRow key={actionKey} label={actionKey} value={actionContextStatus(value)} detail={actionContextType(value)} raw={value} />
                  ))}
                </MiniContextSection>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RequirementPill({ req, compact = false }: { req: WorkflowTraceRequirement; compact?: boolean }) {
  const met = req.met === true
  const label = requirementLabel(req)
  return (
    <div className={cn(
      "rounded-control border px-2 py-1",
      compact ? "text-[11px]" : "text-xs",
      met
        ? "border-status-success/35 bg-status-success/10 text-status-success"
        : "border-status-error/35 bg-status-error/10 text-status-error",
    )}>
      <span className="font-semibold">{met ? "ok" : "blocked"}</span>
      <span className="ml-1 text-app-text">{label}</span>
      {!compact && req.value_preview != null && (
        <code className="ml-1 text-app-muted">{shortJson(req.value_preview)}</code>
      )}
      {!compact && req.turn_result_present === false && req.tool && (
        <span className="ml-1 text-app-muted">waiting for tool result</span>
      )}
    </div>
  )
}

function TraceChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded-control border border-border bg-app-panel px-2 py-1 text-xs">
      <span className="text-app-muted">{label}</span>
      <span className="ml-1 text-app-text">{value}</span>
    </span>
  )
}

function isSkillCardTrace(trace: WorkflowTracePayload): boolean {
  const root = asRecord(trace.skillCards)
  if (stringish(root.engine) === "workflow_v3") return true
  return (Array.isArray(trace.trace) ? trace.trace : []).some((event) => event.event.startsWith("workflow_v3_"))
}

function skillCardForeground(trace: WorkflowTracePayload): string | null {
  return stringish(asRecord(trace.skillCards).foreground)
}

function skillCardStatus(trace: WorkflowTracePayload): string | null {
  return stringish(activeSkillCardSummary(trace).status)
}

function skillCardCards(trace: WorkflowTracePayload): Array<[string, Record<string, unknown>]> {
  const root = asRecord(trace.skillCards)
  const active = asRecord(root.active)
  const cards = Object.entries(active)
    .filter(([, value]) => Object.keys(asRecord(value)).length > 0)
    .map(([key, value]) => [key, asRecord(value)] as [string, Record<string, unknown>])
  if (cards.length > 0) return cards

  const latestState = [...(Array.isArray(trace.trace) ? trace.trace : [])].reverse().find((event) => event.event === "workflow_v3_state")
  const traceCards = asRecord(asRecord(latestState?.details).cards)
  return Object.entries(traceCards)
    .filter(([, value]) => Object.keys(asRecord(value)).length > 0)
    .map(([key, value]) => [key, asRecord(value)] as [string, Record<string, unknown>])
}

function activeSkillCardSummary(trace: WorkflowTracePayload): Record<string, unknown> {
  const foreground = skillCardForeground(trace)
  const cards = skillCardCards(trace)
  if (foreground) {
    const found = cards.find(([key]) => key === foreground)
    if (found) return found[1]
  }
  return cards[0]?.[1] ?? {}
}

function inactiveSkillCardStatus(card: Record<string, unknown>): boolean {
  return ["completed", "declined", "inactive"].includes(stringish(card.status) || "")
}

function requirementLabel(req: WorkflowTraceRequirement): string {
  return req.field || req.tool || req.upload_kind || req.action_key || req.kind || "requirement"
}

function formatTraceEvent(event: string): string {
  return event.replace(/_/g, " ")
}

function shortJson(value: unknown): string {
  const text = typeof value === "string" ? value : JSON.stringify(value)
  if (!text) return ""
  return text.length > 80 ? `${text.slice(0, 80)}...` : text
}

function hoverJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) || ""
  } catch {
    return String(value)
  }
}

function collectedDataRows(trace: WorkflowTracePayload): Array<{ key: string; label: string; value: string; meta?: CollectedDataMeta }> {
  const data = asRecord(trace.collectedData)
  const meta = asRecord(data._field_meta)
  const rows: Array<{ key: string; label: string; value: string; meta?: CollectedDataMeta }> = skillCardCollectedRows(trace)

  Object.entries(asRecord(data.slots)).forEach(([key, value]) => {
    rows.push({ key: `slot.${key}`, label: key, value: shortCollectedValue(value), meta: collectedFieldMeta(meta, key) })
  })

  Object.entries(data).forEach(([key, value]) => {
    if (key === "slots" || key === "endpoints" || key === "_field_meta") return
    rows.push({ key, label: key, value: shortCollectedValue(value), meta: collectedFieldMeta(meta, key) })
  })

  Object.entries(asRecord(data.endpoints)).forEach(([key, value]) => {
    rows.push({ key: `endpoint.${key}`, label: `endpoint.${key}`, value: shortCollectedValue(value) })
  })

  return rows.slice(0, 18)
}

function skillCardCollectedRows(trace: WorkflowTracePayload): Array<{ key: string; label: string; value: string; meta?: CollectedDataMeta }> {
  const card = activeSkillCardSummary(trace)
  const rows: Array<{ key: string; label: string; value: string; meta?: CollectedDataMeta }> = []

  Object.entries(asRecord(card.fields)).forEach(([key, value]) => {
    const row = asRecord(value)
    rows.push({
      key: `skill_card.field.${key}`,
      label: key,
      value: stringish(row.label) || shortCollectedValue(value),
      meta: { source: stringish(row.state) || "field" },
    })
  })

  Object.entries(asRecord(card.data)).forEach(([key, value]) => {
    const row = asRecord(value)
    rows.push({
      key: `skill_card.data.${key}`,
      label: `data.${key}`,
      value: shortCollectedValue(row.result ?? value),
      meta: { source: stringish(row.endpoint) || stringish(row.status) || "loader" },
    })
  })

  Object.entries(asRecord(card.policies)).forEach(([key, value]) => {
    rows.push({
      key: `skill_card.policy.${key}`,
      label: `policy.${key}`,
      value: policyContextVerdict(value) || shortCollectedValue(value),
      meta: { source: "policy" },
    })
  })

  return rows
}

function collectedFieldMeta(meta: Record<string, unknown>, key: string): CollectedDataMeta | undefined {
  const row = asRecord(meta[key])
  if (Object.keys(row).length === 0) return undefined
  const replaced = asRecord(row.replaced)
  return {
    source: stringish(row.source),
    replaced: Object.keys(replaced).length > 0
      ? { value: replaced.value, source: stringish(replaced.source) }
      : null,
  }
}

function workflowActionRows(trace: WorkflowTracePayload): WorkflowActionRow[] {
  const events = Array.isArray(trace.trace) ? trace.trace : []
  const rows = events.flatMap((event) => {
    const row = workflowActionRow(event, trace)
    return row ? [row] : []
  })
  completedActionRows(trace).forEach((row) => {
    if (row.actionKey && rows.some((existing) => existing.actionKey === row.actionKey && existing.status === row.status)) return
    rows.push(row)
  })
  return dedupeEquivalentFieldRows(rows)
}

function workflowInlineRows(trace: WorkflowTracePayload): WorkflowActionRow[] {
  const events = Array.isArray(trace.trace) ? trace.trace : []
  const rows = events.flatMap((event) => {
    const row = workflowActionRow(event, trace)
    if (!row) return []
    if (row.owner === "skill_card_data") return [row]
    if (row.actionType === "field") return [row]
    if (row.actionType === "policy") return [row]
    if (event.event === "action_completed" && stringish(asRecord(event.details).action_key)) return [row]
    if (event.event === "collect_fields_matched") return [row]
    if (event.event === "dropped_unavailable_tool_calls") return [row]
    return []
  })
  completedActionRows(trace).forEach((row) => {
    if (row.actionKey && rows.some((existing) => existing.actionKey === row.actionKey && existing.status === row.status)) return
    rows.push(row)
  })
  return dedupeEquivalentFieldRows(rows)
}

function dedupeEquivalentFieldRows(rows: WorkflowActionRow[]): WorkflowActionRow[] {
  const seen = new Set<string>()
  return rows.filter((row) => {
    const signature = fieldCommitSignature(row)
    if (!signature) return true
    if (seen.has(signature)) return false

    seen.add(signature)
    return true
  })
}

function fieldCommitSignature(row: WorkflowActionRow): string | null {
  if (row.actionType !== "field") return null
  if (row.status === "error") return null

  return [
    row.turnNumber ?? "",
    row.label,
    row.status,
    row.detail ?? "",
  ].join("|")
}

function workflowActionRow(event: WorkflowTraceEvent, trace: WorkflowTracePayload): WorkflowActionRow | null {
  const details = asRecord(event.details)
  const eventName = event.event
  if (![
    "action_completed",
    "action_failed",
    "field_committed",
    "field_invalidated",
    "model_tool_action_waiting",
    "model_tool_action_completed",
    "collect_fields_matched",
    "dropped_unavailable_tool_calls",
    "workflow_error",
  ].includes(eventName)) {
    return null
  }

  const result = asRecord(details.result)
  if (eventName === "action_completed" && stringish(result.status) === "failed") return null

  const owner = stringish(details.owner)
  const field = stringish(details.field)
  const fieldState = stringish(details.field_state) || stringish(details.state) || stringish(result.status)
  const fieldLabel = stringish(details.label) || stringish(details.value)
  const actionKey = stringish(details.action_key)
  const endpoint = stringish(details.endpoint) || stringish(result.endpoint)
  const tool = stringish(details.tool) || stringish(result.tool)
  const actionType = stringish(details.action_type) || stringish(result.status)
  const policy = stringish(details.policy) || stringish(result.policy)
  const isPolicy = actionType === "policy" || tool === "policy" || !!policy
  const status: WorkflowActionRow["status"] =
    eventName === "field_invalidated" ? "error"
    : eventName === "field_committed" && fieldState && !["confirmed", "locked"].includes(fieldState) ? "waiting"
    : eventName === "workflow_error" || eventName === "action_failed" || eventName === "dropped_unavailable_tool_calls" ? "error"
    : eventName === "model_tool_action_waiting" ? "waiting"
    : "ok"

  let label = formatTraceEvent(eventName)
  if (eventName === "field_committed" && field) label = `save ${field}`
  else if (eventName === "field_invalidated" && field) label = `reject ${field}`
  else if (eventName === "collect_fields_matched") label = "collected fields"
  else if (isPolicy) label = `policy ${policy || actionKey || ""}`.trim()
  else if (owner === "skill_card_data" && endpoint) label = `fetch ${endpoint}`
  else if (owner === "tool_dispatch" && endpoint) label = `tool call ${endpoint}`
  else if (owner === "skill_card_action" && actionKey) label = `workflow action ${actionKey}`
  else if (tool) label = `tool ${tool}`
  else if (endpoint) label = `fetch ${endpoint}`
  else if (actionKey) label = `workflow action ${actionKey}`
  else if (actionType) label = actionType

  const detail =
    eventName === "field_committed" ? [fieldState, fieldLabel].filter(Boolean).join(" ") || null
    : eventName === "field_invalidated" ? stringish(details.reason) || fieldState || null
    : eventName === "collect_fields_matched" ? objectPairs(details.fields)
    : eventName === "dropped_unavailable_tool_calls" ? arrayLabel(details.dropped_tools)
    : owner === "skill_card_data" ? stringish(result.status) || null
    : owner === "tool_dispatch" && tool ? tool
    : tool && endpoint ? endpoint
    : actionKey ? actionType || stringish(details.owner) || null
    : stringish(details.owner) || null

  return {
    key: `${event.seq}-${eventName}`,
    label,
    detail,
    status,
    turnNumber: event.turn_number,
    actionKey,
    actionType,
    owner,
    endpoint,
    tool,
    payload: workflowActionPayload(eventName, endpoint, isPolicy, details, trace),
  }
}

function completedActionRows(trace: WorkflowTracePayload): WorkflowActionRow[] {
  const completed = asRecord(asRecord(trace.state).completed_actions)
  return Object.entries(completed).map(([actionKey, value], index) => {
    const row = asRecord(value)
    const actionType = stringish(row.action_type) || workflowActionTypeFromKey(actionKey)
    const statusText = stringish(row.status)
    return {
      key: `completed-${index}-${actionKey}`,
      label: `workflow action ${actionKey}`,
      detail: actionType,
      status: statusText === "failed" ? "error" : "ok",
      turnNumber: typeof row.turn === "number" ? row.turn : typeof row.turn_number === "number" ? row.turn_number : null,
      actionKey,
      actionType,
      owner: "completed_actions",
      payload: Object.keys(row).length > 0 ? row : value,
    }
  })
}

function workflowActionTypeFromKey(actionKey: string): string {
  if (/^escalat(e|ion)|_escalat(e|ion)/.test(actionKey)) return "escalate"
  if (/^request_upload|_request_upload|upload/.test(actionKey)) return "request_upload"
  if (/^submit_feedback|_submit_feedback|feedback/.test(actionKey)) return "submit_feedback"
  if (/^set_custom_field|_set_custom_field/.test(actionKey)) return "set_custom_field"
  return actionKey
}

function workflowActionPayload(eventName: string, endpoint: string | null, isPolicy: boolean, details: Record<string, unknown>, trace: WorkflowTracePayload): unknown {
  if (eventName === "action_completed" && stringish(details.tool)) return details
  if (eventName === "action_completed" && stringish(details.owner) === "skill_card_data") {
    const actionKey = stringish(details.action_key)
    return actionKey ? asRecord(activeSkillCardSummary(trace).data)[actionKey] ?? details : details
  }
  if (endpoint && eventName === "action_completed") {
    const collected = asRecord(trace.collectedData)
    const endpoints = asRecord(collected.endpoints)
    return simplifyEndpointActionPayload(endpoints[endpoint] ?? collected[endpoint] ?? details, endpoint)
  }
  if (endpoint && eventName === "action_failed") return simplifyEndpointActionPayload(details.error ?? details, endpoint)
  if (isPolicy && eventName === "action_completed") {
    const result = asRecord(details.result)
    return result.data ?? result ?? details
  }
  if (isPolicy && eventName === "action_failed") return details.error ?? details
  if (eventName === "field_committed" || eventName === "field_invalidated") return details
  if (eventName === "collect_fields_matched") return details.fields ?? details
  if (eventName === "dropped_unavailable_tool_calls") return {
    dropped_tools: details.dropped_tools,
    available_tools: details.available_tools,
    tool_calls: details.tool_calls,
  }
  return details
}

function simplifyEndpointActionPayload(value: unknown, endpoint: string): unknown {
  const payload = asRecord(value)
  const result = asRecord(payload.result)
  const source = Object.keys(result).length > 0 ? result : payload
  const upstreamBody = source.upstream_body ?? source.body
  const statusCode = source.status_code ?? source.status
  if (upstreamBody == null) return value

  return {
    endpoint: stringish(source.endpoint) ?? endpoint,
    status_code: statusCode,
    upstream_body: upstreamBody,
  }
}

function conversationItems(messages: ChatBubble[], workflowRows: WorkflowActionRow[]): ConversationItem[] {
  const rowsByTurn = new Map<number, WorkflowActionRow[]>()
  const undatedRows: WorkflowActionRow[] = []
  const toolMessagesByTurn = new Map<number, ChatBubble[]>()
  const toolCallMessagesByTurn = new Map<number, ChatBubble[]>()
  workflowRows.forEach((row) => {
    if (typeof row.turnNumber === "number") {
      const rows = rowsByTurn.get(row.turnNumber) ?? []
      rows.push(row)
      rowsByTurn.set(row.turnNumber, rows)
    } else {
      undatedRows.push(row)
    }
  })
  messages.forEach((message) => {
    if (!isToolCallOnlyAssistantMessage(message) || typeof message.turnNumber !== "number") return
    const rows = toolCallMessagesByTurn.get(message.turnNumber) ?? []
    rows.push(message)
    toolCallMessagesByTurn.set(message.turnNumber, rows)
  })
  messages.forEach((message) => {
    if (message.role !== "tool" || typeof message.turnNumber !== "number") return
    const rows = toolMessagesByTurn.get(message.turnNumber) ?? []
    rows.push(mergeToolCallsIntoToolMessage(message, toolCallMessagesByTurn.get(message.turnNumber) ?? []))
    toolMessagesByTurn.set(message.turnNumber, rows)
  })

  const used = new Set<string>()
  const usedMessages = new Set<number>()
  const items: ConversationItem[] = undatedRows.map((row) => {
    used.add(row.key)
    return { kind: "workflow", row }
  })

  messages.forEach((message) => {
    if (usedMessages.has(message.id)) return
    if (isToolCallOnlyAssistantMessage(message)) {
      usedMessages.add(message.id)
      return
    }

    items.push({ kind: "message", message })
    usedMessages.add(message.id)
    if (message.role !== "user" || typeof message.turnNumber !== "number") return

    const rows = rowsByTurn.get(message.turnNumber) ?? []
    rows.forEach((row) => {
      used.add(row.key)
      items.push({ kind: "workflow", row })
    })

    const toolMessages = toolMessagesByTurn.get(message.turnNumber) ?? []
    toolMessages.forEach((toolMessage) => {
      if (usedMessages.has(toolMessage.id)) return
      usedMessages.add(toolMessage.id)
      items.push({ kind: "message", message: toolMessage })
    })
  })

  workflowRows.forEach((row) => {
    if (!used.has(row.key)) items.push({ kind: "workflow", row })
  })

  return items
}

function isToolCallOnlyAssistantMessage(message: ChatBubble): boolean {
  return message.role === "assistant" &&
    !message.content?.trim() &&
    Array.isArray(message.toolCalls) &&
    message.toolCalls.length > 0 &&
    (!Array.isArray(message.toolResults) || message.toolResults.length === 0)
}

function mergeToolCallsIntoToolMessage(toolMessage: ChatBubble, callMessages: ChatBubble[]): ChatBubble {
  const calls = callMessages.flatMap((message) => Array.isArray(message.toolCalls) ? message.toolCalls : [])
  if (calls.length === 0) return toolMessage

  const existingCalls = Array.isArray(toolMessage.toolCalls) ? toolMessage.toolCalls : []
  const matchingCalls = matchingToolCallsForResults(calls, toolMessage.toolResults)
  return { ...toolMessage, toolCalls: [...existingCalls, ...matchingCalls] }
}

function matchingToolCallsForResults(calls: unknown[], results: unknown[] | undefined): unknown[] {
  const ids = new Set(
    Array.isArray(results)
      ? results.map((result) => stringish(asRecord(result).id)).filter((id): id is string => !!id)
      : []
  )
  if (ids.size === 0) return calls

  const matched = calls.filter((call) => {
    const id = stringish(asRecord(call).id)
    return !!id && ids.has(id)
  })
  return matched.length > 0 ? matched : calls
}

function shortCollectedValue(value: unknown): string {
  const record = asRecord(value)
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`
  if (Array.isArray(record.installed_offers)) return `${record.installed_offers.length} offer${record.installed_offers.length === 1 ? "" : "s"}`
  if (record.ok === true && Object.keys(record).length === 1) return "ok"
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value)
  const keys = Object.keys(record)
  if (keys.length > 0) return keys.slice(0, 4).join(", ")
  return shortJson(value)
}

function valueKind(value: unknown): string | null {
  if (value == null) return "empty"
  if (Array.isArray(value)) return "list"
  if (typeof value === "object") return "object"
  return typeof value
}

function objectPairs(value: unknown): string | null {
  const record = asRecord(value)
  const text = Object.entries(record).map(([key, val]) => `${key}=${shortCollectedValue(val)}`).join(", ")
  return text || null
}

function arrayLabel(value: unknown): string | null {
  return Array.isArray(value) ? value.map(String).join(", ") : stringish(value)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function stringish(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : value == null ? null : String(value)
}

function RunDetailsTab({ run, scenario, loading }:
  { run: TestRunDetail | null; scenario: TestLabScenario; loading: boolean }) {
  const { props } = usePage<SharedProps>()
  const isAdmin = props.isAdmin === true

  if (scenario.latestRunId == null) {
    return <div className="text-sm text-app-muted">No runs yet.</div>
  }
  if (loading && !run) {
    return <div className="text-sm text-app-muted">Loading…</div>
  }
  const dur = run?.durationMs ?? scenario.latestRunDurationMs
  const cost = run?.costUsd ?? scenario.latestRunCostUsd
  const snapshot = run?.agentSnapshot
  const hasSnapshot = snapshot && Object.keys(snapshot).length > 0
  return (
    <div className="space-y-3 text-sm">
      <Row label="Status"     value={(run?.status ?? scenario.latestRunStatus ?? "—") as string} />
      <Row label="Duration"   value={dur != null ? `${(dur / 1000).toFixed(1)}s` : "—"} />
      {isAdmin && (
        <>
          <Row label="Cost"   value={cost != null ? `$${cost.toFixed(4)}` : "—"} />
          <Row label="Tokens" value={run ? `${run.inputTokens ?? 0} in / ${run.outputTokens ?? 0} out` : "—"} />
          <ModelRequestsBlock summary={run?.modelRequests ?? null} />
        </>
      )}
      <Row label="Started"    value={run?.startedAt ?? "—"} />
      <Row label="Finished"   value={run?.finishedAt ?? scenario.latestRunAt ?? "—"} />
      {run?.summary && (
        <div className="pt-3">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-app-muted">Judge summary</div>
          <p className="whitespace-pre-wrap rounded-card border border-border bg-app-bg p-3 text-app-muted">{run.summary}</p>
        </div>
      )}
      {hasSnapshot && (
        <div className="pt-3">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-app-muted">Agent context</div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-card border border-border bg-app-bg p-3 text-[11px] text-app-muted">
            {JSON.stringify(snapshot, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

function ModelRequestsBlock({ summary }: { summary: TestRunModelRequestSummary | null }) {
  const groups = summary?.byModel ?? []
  const requests = summary?.requests ?? []
  const total = summary?.totalRequests ?? 0
  return (
    <div className="pt-2">
      <div className="mb-2 flex items-center justify-between gap-3 border-b border-border/40 pb-1">
        <span className="text-[10px] uppercase tracking-wide text-app-muted">Model requests</span>
        <span className="text-app-text">{summary ? `${total} request${total === 1 ? "" : "s"}` : "—"}</span>
      </div>
      {summary?.error && (
        <div className="mb-2 rounded-card border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
          <div>{summary.error.message}</div>
          {summary.error.detail && <div className="mt-1 font-mono text-[11px] text-red-100/80">{summary.error.detail}</div>}
        </div>
      )}
      {summary && groups.length === 0 && !summary.error && (
        <div className="rounded-card border border-border bg-app-bg p-3 text-xs text-app-muted">No model request rows captured.</div>
      )}
      {groups.length > 0 && (
        <div className="overflow-hidden rounded-card border border-border bg-app-bg">
          {groups.map((group) => (
            <div key={`${group.modelVersionId ?? "unknown"}-${group.modelKey}`} className="border-b border-border/50 p-3 last:border-b-0">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-app-text">{group.modelIdentifier || group.modelName || group.modelKey}</div>
                  <div className="mt-0.5 text-[11px] text-app-muted">{group.providerName || group.providerKey || "Unknown provider"} · {compactCounts(group.kinds)}</div>
                </div>
                <div className="text-right text-[11px] text-app-muted">
                  <div className="text-app-text">{group.requestCount} request{group.requestCount === 1 ? "" : "s"}</div>
                  <div>{group.inputTokens} in / {group.outputTokens} out · {formatUsd(group.costUsd)}</div>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-app-muted">
                <span>cache write {group.cacheCreationInputTokens}</span>
                <span>cache read {group.cacheReadInputTokens}</span>
                {group.latencyMs != null && <span>avg {group.latencyMs}ms</span>}
                <span>{compactCounts(group.statuses)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {requests.length > 0 && (
        <details className="mt-2 rounded-card border border-border bg-app-bg p-3">
          <summary className="cursor-pointer text-xs font-medium text-app-text">Request log</summary>
          <div className="mt-2 divide-y divide-border/50">
            {requests.map((request) => (
              <div key={request.id} className="py-2 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="font-medium text-app-text">{request.kind} · {request.status}</span>
                  <span className="text-app-muted">{request.modelIdentifier || request.modelName || request.modelKey}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-app-muted">
                  <span>{request.inputTokens} in / {request.outputTokens} out</span>
                  <span>{formatUsd(request.costUsd)}</span>
                  {request.latencyMs != null && <span>{request.latencyMs}ms</span>}
                  {request.createdAt && <span>{request.createdAt}</span>}
                </div>
                {request.errorMessage && <div className="mt-1 rounded border border-red-500/30 bg-red-500/10 p-2 text-[11px] text-red-100">{request.errorMessage}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

function compactCounts(counts: Record<string, number> | null | undefined): string {
  const entries = Object.entries(counts ?? {}).filter(([, count]) => count > 0)
  if (entries.length === 0) return "none"
  return entries.map(([key, count]) => `${key} ${count}`).join(", ")
}

function formatUsd(value: number | null | undefined): string {
  return `$${Number(value ?? 0).toFixed(4)}`
}

function isActiveRunStatus(status: string | null | undefined): boolean {
  return ACTIVE_RUN_STATUSES.has(status ?? "")
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/40 pb-1">
      <span className="text-[10px] uppercase tracking-wide text-app-muted">{label}</span>
      <span className="text-app-text">{value}</span>
    </div>
  )
}

// Per-scenario partner-API mock editor. Test runs require explicit mocks
// for endpoint calls, keyed by CompanyEndpoint.key. JsonEditor lints as
// the operator types so JSON typos surface inline instead of at save.
function MocksTab({ scenario, scenarioUpdatePath }: { scenario: TestLabScenario; scenarioUpdatePath?: (scenarioId: number) => string }) {
  const initial = JSON.stringify(scenario.endpointMockOverrides ?? {}, null, 2)
  const [text, setText]       = useState<string>(initial)
  const [saving, setSaving]   = useState(false)
  const [status, setStatus]   = useState<{ kind: "ok" | "err"; msg: string } | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  useEffect(() => {
    setText(JSON.stringify(scenario.endpointMockOverrides ?? {}, null, 2))
    setStatus(null)
  }, [scenario.id, scenario.endpointMockOverrides])

  async function save() {
    setSaving(true)
    setStatus(null)
    try {
      JSON.parse(text || "{}")
    } catch (e) {
      setStatus({ kind: "err", msg: `Invalid JSON: ${(e as Error).message}` })
      setSaving(false)
      return
    }
    if (!scenarioUpdatePath) {
      setStatus({ kind: "err", msg: "This scenario's mocks can't be edited here." })
      setSaving(false)
      return
    }
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const res = await fetch(scenarioUpdatePath(scenario.id), {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ endpoint_mock_overrides: text }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string }
        setStatus({ kind: "err", msg: err.error ?? `HTTP ${res.status}` })
      } else {
        setStatus({ kind: "ok", msg: "Saved." })
      }
    } catch (e) {
      setStatus({ kind: "err", msg: (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-start justify-between gap-3 text-xs text-app-muted">
        <span>
          JSON object keyed by <code className="rounded bg-white/5 px-1">endpoint.key</code>.
          Missing endpoint mocks make test runs fail loudly.
        </span>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="inline-flex shrink-0 items-center gap-1 rounded-control border border-border px-2 py-0.5 text-[11px] text-app-muted transition hover:bg-white/5 hover:text-app-text"
          title="What goes in this box?"
        >
          <Info size={12} strokeWidth={2} />
          What is this?
        </button>
      </div>

      <JsonEditor value={text} onChange={setText} minHeight="320px" />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-control border border-brand-red bg-brand-red px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-redDark disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save mocks"}
        </button>
        {status && (
          <span className={cn("text-xs", status.kind === "ok" ? "text-status-success" : "text-status-error")}>
            {status.msg}
          </span>
        )}
      </div>

      <MocksHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}

// Plain-language explainer. Goal: an operator who's never edited a mock
// before should grok the structure in <30 seconds. Pulls active endpoints
// from /endpoints (Inertia JSON) so the table reflects this tenant's
// reality, not a hardcoded example.
function MocksHelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [endpoints, setEndpoints] = useState<Array<{ key: string; httpMethod: string; path: string; active: boolean }>>([])
  const [loading, setLoading]     = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    fetch("/endpoints", { credentials: "same-origin", headers: { "X-Inertia": "true", Accept: "application/json" } })
      .then((r) => r.json())
      .then((payload: { props?: { endpoints?: Array<Record<string, unknown>> } }) => {
        const list = (payload?.props?.endpoints ?? [])
          .map((e) => ({
            key:        String(e.key ?? ""),
            httpMethod: String(e.httpMethod ?? "GET"),
            path:       String(e.pathTemplate ?? e.path ?? ""),
            active:     true,
          }))
          .filter((e) => e.key)
        setEndpoints(list)
      })
      .catch(() => setEndpoints([]))
      .finally(() => setLoading(false))
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <header className="flex items-start gap-3 border-b border-border px-6 py-4">
        <Info size={18} className="mt-0.5 shrink-0 text-app-muted" />
        <div className="flex-1">
          <h2 className="text-base font-semibold">What goes in the Mocks box?</h2>
          <p className="mt-0.5 text-xs text-app-muted">A pretend-answer the agent gets when it asks the partner during this test.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 rounded-control p-1.5 text-app-subtle transition hover:bg-white/5 hover:text-app-text">
          <X size={16} />
        </button>
      </header>
      <div className="space-y-4 px-6 py-5 text-sm text-app-text">
        <section>
          <p>
            Imagine the agent has a phone book of partners it can call (each row in <strong>Endpoints</strong>).
            When a test runs, we don't actually call them — we hand back a pretend answer so the test is
            fast and predictable. <strong>That pretend answer is what you put here.</strong>
          </p>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Active endpoints in this company</h3>
          <p className="text-xs text-app-muted">Use the <code className="rounded bg-white/5 px-1">key</code> column as the JSON key in your Mocks box.</p>
          {loading ? (
            <div className="mt-2 text-xs text-app-subtle">Loading…</div>
          ) : endpoints.length === 0 ? (
            <div className="mt-2 text-xs text-app-subtle">No active endpoints configured for this company.</div>
          ) : (
            <div className="mt-2 overflow-hidden rounded-control border border-border">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03] text-app-subtle">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Key</th>
                    <th className="px-3 py-1.5 text-left font-medium">HTTP</th>
                    <th className="px-3 py-1.5 text-left font-medium">Path</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {endpoints.map((e) => (
                    <tr key={e.key}>
                      <td className="px-3 py-1.5 font-mono text-app-text">{e.key}</td>
                      <td className="px-3 py-1.5 font-mono text-app-muted">{e.httpMethod}</td>
                      <td className="px-3 py-1.5 font-mono text-app-muted">{e.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Shape</h3>
          <pre className="mt-1 overflow-x-auto rounded-control border border-border bg-app-bg p-3 font-mono text-[11px] text-app-text">{`{
  "coin_balance": { "coins": 42, "usd": "0.84" },
  "rewards_summary": { "total_earned_usd": "12.00", "pending": [], "recent": [] }
}`}</pre>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Rules of thumb</h3>
          <ul className="list-disc space-y-1 pl-5 text-xs text-app-muted">
            <li>Use the endpoint <strong>nickname / key</strong> — never the numeric id.</li>
            <li>Use keys from the endpoint catalog; unknown keys are rejected on save.</li>
            <li>Include every endpoint this scenario can call.</li>
            <li>An empty object <code className="rounded bg-white/5 px-1">{`{}`}</code> means this scenario should not call endpoints.</li>
            <li>The shape of each value should match what the real endpoint would return.</li>
          </ul>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Why bother?</h3>
          <p className="text-xs text-app-muted">
            To stage a specific situation. Want to test what the agent does when the user has zero coins?
            Mock <code className="rounded bg-white/5 px-1">coin_balance</code> with <code className="rounded bg-white/5 px-1">{`{"coins": 0}`}</code>.
            Want to test the "huge balance" path? Mock it with <code className="rounded bg-white/5 px-1">{`{"coins": 999999}`}</code>.
          </p>
        </section>
      </div>
      <footer className="flex justify-end border-t border-border px-6 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-4 py-1.5 text-sm text-app-text transition hover:bg-white/5"
        >
          Got it
        </button>
      </footer>
    </Dialog>
  )
}
